import type {FetchArgs} from '@reduxjs/toolkit/query/react';

import {AUTO_DELIVERY_DETAILS, AUTO_DELIVERY_DETAILS_EXTENDED} from '@/consts/icc/iccMethods';
import {selectGigyaToken} from '@/state/selectors/auth';
import {dispatch, getState} from '@/state/store';
import type {AutoDeliveryDetails, AutoDeliveryDetailsDcx} from '@/types/icc/autoDeliveryDetails';
import objUtils from '@/utils/objUtils';

import {ICC_API_TAGS, iccApi} from './iccApi';

type UpdateAutoDeliveryDetailsResponse = {
    AutoDelivery: Record<string, unknown>;
};

const getAutoDeliveryQuery =
    (url: string, isDcx = false) =>
    (): string | FetchArgs => {
        const state = getState?.();
        const gigyaToken = isDcx && state ? selectGigyaToken(state) : null;

        if (gigyaToken) {
            return {
                url,
                headers: {
                    et: gigyaToken,
                },
            };
        }

        return url;
    };

const consumerApi = iccApi.injectEndpoints({
    endpoints: (builder) => ({
        getAutoDeliveryDetails: builder.query<AutoDeliveryDetails, void>({
            query: getAutoDeliveryQuery(AUTO_DELIVERY_DETAILS),
            transformResponse: (response: unknown) => objUtils.objectKeysToLowerCase(response) as AutoDeliveryDetails,
        }),
        getAutoDeliveryDetailsDcx: builder.query<AutoDeliveryDetailsDcx, void>({
            query: getAutoDeliveryQuery(AUTO_DELIVERY_DETAILS, true),
            transformResponse: (response: unknown) =>
                objUtils.objectKeysToLowerCase(response) as AutoDeliveryDetailsDcx,
            providesTags: [ICC_API_TAGS.AUTO_DELIVERY_DCX],
        }),
        getAutoDeliveryDetailsExtended: builder.query<AutoDeliveryDetails, void>({
            query: getAutoDeliveryQuery(AUTO_DELIVERY_DETAILS_EXTENDED),
            transformResponse: (response: unknown) => objUtils.objectKeysToLowerCase(response) as AutoDeliveryDetails,
        }),
        getAutoDeliveryDetailsExtendedDcx: builder.query<AutoDeliveryDetailsDcx, void>({
            query: getAutoDeliveryQuery(AUTO_DELIVERY_DETAILS_EXTENDED, true),
            transformResponse: (response: unknown) =>
                objUtils.objectKeysToLowerCase(response) as AutoDeliveryDetailsDcx,
            providesTags: [ICC_API_TAGS.AUTO_DELIVERY_DCX],
            // Sync the data from the extended query to the /details query
            onQueryStarted: async (_, {dispatch, queryFulfilled}) => {
                try {
                    const {data} = await queryFulfilled;

                    dispatch(
                        consumerApi.util.updateQueryData('getAutoDeliveryDetailsDcx', undefined, (details) => {
                            return {...details, ...data};
                        })
                    );
                } catch {
                    // noop: no cache sync needed if request fails
                }
            },
        }),
    }),
});

const POLLING_INTERVAL = 30 * 60 * 1000;

type AutoDeliveryDetailsQueryOptions = Parameters<typeof consumerApi.useGetAutoDeliveryDetailsQuery>[1];
type AutoDeliveryDetailsDcxQueryOptions = Parameters<typeof consumerApi.useGetAutoDeliveryDetailsDcxQuery>[1];
type AutoDeliveryDetailsExtendedQueryOptions = Parameters<typeof consumerApi.useGetAutoDeliveryDetailsExtendedQuery>[1];
type AutoDeliveryDetailsExtendedDcxQueryOptions = Parameters<
    typeof consumerApi.useGetAutoDeliveryDetailsExtendedDcxQuery
>[1];
const AUTO_DELIVERY_QUERY_BASE_OPTIONS = {pollingInterval: POLLING_INTERVAL};

const withAutoDeliveryDefaults = (options?: AutoDeliveryDetailsQueryOptions): AutoDeliveryDetailsQueryOptions => ({
    ...AUTO_DELIVERY_QUERY_BASE_OPTIONS,
    ...options,
});

const withAutoDeliveryExtendedDefaults = (
    options?: AutoDeliveryDetailsExtendedQueryOptions
): AutoDeliveryDetailsExtendedQueryOptions => ({
    ...AUTO_DELIVERY_QUERY_BASE_OPTIONS,
    ...options,
});

export const useGetAutoDeliveryDetails = (options?: AutoDeliveryDetailsQueryOptions) => {
    return consumerApi.useGetAutoDeliveryDetailsQuery(undefined, withAutoDeliveryDefaults(options));
};

export const useGetAutoDeliveryDetailsLazyQuery = consumerApi.endpoints.getAutoDeliveryDetails.useLazyQuery;

const withAutoDeliveryDcxDefaults = (
    options?: AutoDeliveryDetailsDcxQueryOptions
): AutoDeliveryDetailsDcxQueryOptions => ({
    ...AUTO_DELIVERY_QUERY_BASE_OPTIONS,
    ...options,
});

const withAutoDeliveryExtendedDcxDefaults = (
    options?: AutoDeliveryDetailsExtendedDcxQueryOptions
): AutoDeliveryDetailsExtendedDcxQueryOptions => ({
    ...AUTO_DELIVERY_QUERY_BASE_OPTIONS,
    ...options,
});

export const useGetAutoDeliveryDetailsDcx = (options?: AutoDeliveryDetailsDcxQueryOptions) => {
    return consumerApi.useGetAutoDeliveryDetailsDcxQuery(undefined, withAutoDeliveryDcxDefaults(options));
};

export const useGetAutoDeliveryDetailsDcxStatus = () => {
    const details = consumerApi.endpoints.getAutoDeliveryDetailsDcx.useQueryState(undefined, {
        selectFromResult: ({isLoading, isFetching, isError}) => ({isLoading, isFetching, isError}),
    });
    const extended = consumerApi.endpoints.getAutoDeliveryDetailsExtendedDcx.useQueryState(undefined, {
        selectFromResult: ({isLoading, isFetching}) => ({isLoading, isFetching}),
    });

    return {
        isLoading: details.isLoading || extended.isLoading,
        isFetching: details.isFetching || extended.isFetching,
        isError: details.isError,
    };
};

export const useGetAutoDeliveryDetailsDcxLazyQuery = consumerApi.endpoints.getAutoDeliveryDetailsDcx.useLazyQuery;

export const useGetAutoDeliveryDetailsExtended = (options?: AutoDeliveryDetailsExtendedQueryOptions) => {
    return consumerApi.useGetAutoDeliveryDetailsExtendedQuery(undefined, withAutoDeliveryExtendedDefaults(options));
};

export const useGetAutoDeliveryDetailsExtendedLazyQuery =
    consumerApi.endpoints.getAutoDeliveryDetailsExtended.useLazyQuery;

export const useGetAutoDeliveryDetailsExtendedDcx = (options?: AutoDeliveryDetailsExtendedDcxQueryOptions) => {
    return consumerApi.useGetAutoDeliveryDetailsExtendedDcxQuery(
        undefined,
        withAutoDeliveryExtendedDcxDefaults(options)
    );
};

export const useGetAutoDeliveryDetailsExtendedDcxLazyQuery =
    consumerApi.endpoints.getAutoDeliveryDetailsExtendedDcx.useLazyQuery;

export const updateAutoDeliveryDetails = (responseData: UpdateAutoDeliveryDetailsResponse) => {
    const mappedAutoDelivery = objUtils.objectKeysToLowerCase(responseData.AutoDelivery);
    const assignDetails = (details: AutoDeliveryDetails) => {
        details.autoDelivery = mappedAutoDelivery as AutoDeliveryDetails['autoDelivery'];
    };

    dispatch?.(consumerApi.util.updateQueryData('getAutoDeliveryDetails', undefined, assignDetails));
    dispatch?.(consumerApi.util.updateQueryData('getAutoDeliveryDetailsExtended', undefined, assignDetails));
};

export const resetAutoDeliveryDetailsDcx = () => {
    dispatch?.(iccApi.util.invalidateTags([ICC_API_TAGS.AUTO_DELIVERY_DCX]));
};
